/**
 * ADMIN DEBUG MENU - Phase 8: Admin & Economy Final Check
 * 
 * Hidden admin panel accessed by long-pressing the logo for 3 seconds.
 * Features:
 * - Global Reset: Clear all localStorage
 * - Force Win: Trigger victory animation
 * - Logs View: See recent transactions
 * - Economy Overview: View all prices
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Settings, Trash2, Trophy, FileText, X, DollarSign, 
  RefreshCw, AlertTriangle, CheckCircle, Copy, Zap,
  Coins, Store, Swords
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';

// Economy Configuration (for display)
// PHASE 9-B: MICRO-TRANSACTION ACTIVATION
const ECONOMY_CONFIG = {
  tokenBundles: [
    { id: 'tester', tokens: 250, price: 0.02, bonus: 0, label: '⚡ TRY IT OUT' },
    { id: 'starter', tokens: 600, price: 0.05, bonus: 50 },
    { id: 'pro', tokens: 1300, price: 0.1, bonus: 100 },
    { id: 'whale', tokens: 7000, price: 0.5, bonus: 500 },
  ],
  mysteryPacks: [
    { name: 'Basic', cost: 50 },
    { name: 'Premium', cost: 250 },
    { name: 'Elite', cost: 500 },
    { name: 'Legendary', cost: 1000 },
  ],
  arenaDegenCosts: {
    casual: 10,       // Spam-able
    ranked: 100,      // Competitive
    highStakes: 500   // Whale tier
  },
  fees: {
    marketplace: '5%',
    swap: '1%',
    withdrawal: '0%'
  }
};

// Transaction Log Storage
const TRANSACTION_LOG_KEY = 'admin_transaction_logs';

export const logTransaction = (type, details) => {
  try {
    const logs = JSON.parse(localStorage.getItem(TRANSACTION_LOG_KEY) || '[]');
    logs.unshift({
      timestamp: new Date().toISOString(),
      type,
      details,
      id: Date.now()
    });
    // Keep only last 50 logs
    localStorage.setItem(TRANSACTION_LOG_KEY, JSON.stringify(logs.slice(0, 50)));
  } catch (e) {
    console.error('Failed to log transaction:', e);
  }
};

// Admin Menu Modal
const AdminMenuModal = ({ isOpen, onClose, onForceWin }) => {
  const [activeTab, setActiveTab] = useState('reset');
  const [logs, setLogs] = useState([]);
  const [resetConfirm, setResetConfirm] = useState(false);

  useEffect(() => {
    if (isOpen && activeTab === 'logs') {
      const storedLogs = JSON.parse(localStorage.getItem(TRANSACTION_LOG_KEY) || '[]');
      setLogs(storedLogs);
    }
  }, [isOpen, activeTab]);

  const handleGlobalReset = () => {
    if (!resetConfirm) {
      setResetConfirm(true);
      return;
    }

    // Get keys to preserve
    const preserveKeys = ['helius_rpc_url'];
    const preserved = {};
    preserveKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) preserved[key] = value;
    });

    // Clear all localStorage
    localStorage.clear();

    // Restore preserved keys
    Object.entries(preserved).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });

    toast.success('🧹 Global Reset Complete!', {
      description: 'All user data cleared. Refresh to start fresh.'
    });

    setResetConfirm(false);
    
    // Reload after short delay
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  const handleForceWin = () => {
    onForceWin?.();
    toast.success('🏆 Victory Animation Triggered!');
    onClose();
  };

  const handleClearLogs = () => {
    localStorage.removeItem(TRANSACTION_LOG_KEY);
    setLogs([]);
    toast.success('Logs cleared!');
  };

  const handleCopyLogs = () => {
    const logText = logs.map(l => 
      `[${l.timestamp}] ${l.type}: ${JSON.stringify(l.details)}`
    ).join('\n');
    navigator.clipboard.writeText(logText);
    toast.success('Logs copied to clipboard!');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-md p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-[#0a0a0a] border-2 border-red-500/50 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-red-500/30 bg-red-500/10">
            <div className="flex items-center gap-3">
              <Settings className="w-6 h-6 text-red-400 animate-spin-slow" />
              <h2 className="font-anton text-xl text-white">ADMIN DEBUG MENU</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white p-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-white/10">
            {[
              { id: 'reset', icon: Trash2, label: 'Reset' },
              { id: 'economy', icon: DollarSign, label: 'Economy' },
              { id: 'tools', icon: Zap, label: 'Tools' },
              { id: 'logs', icon: FileText, label: 'Logs' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-bold transition-colors ${
                  activeTab === tab.id
                    ? 'text-red-400 bg-red-500/10 border-b-2 border-red-500'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[50vh]">
            {/* RESET TAB */}
            {activeTab === 'reset' && (
              <div className="space-y-4">
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-yellow-400 font-bold">Global Reset</p>
                      <p className="text-yellow-200/70 text-sm mt-1">
                        This will clear ALL user data including wallet, inventory, and game progress.
                        The Helius RPC key will be preserved.
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleGlobalReset}
                  className={`w-full py-4 font-bold ${
                    resetConfirm
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-red-500/20 border border-red-500/50 text-red-400 hover:bg-red-500/30'
                  }`}
                >
                  <Trash2 className="w-5 h-5 mr-2" />
                  {resetConfirm ? 'CONFIRM RESET - CLICK AGAIN' : 'GLOBAL RESET'}
                </Button>

                {resetConfirm && (
                  <p className="text-red-400 text-xs text-center animate-pulse">
                    ⚠️ Click again to confirm. This cannot be undone!
                  </p>
                )}

                {/* Quick Clear Options */}
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <button
                    onClick={() => {
                      localStorage.removeItem('player_inventory');
                      toast.success('Inventory cleared!');
                    }}
                    className="py-2 bg-white/5 hover:bg-white/10 rounded-lg text-white/70 text-sm"
                  >
                    Clear Inventory
                  </button>
                  <button
                    onClick={() => {
                      localStorage.removeItem('user_pin');
                      toast.success('PIN cleared! Refresh to reset.');
                    }}
                    className="py-2 bg-white/5 hover:bg-white/10 rounded-lg text-white/70 text-sm"
                  >
                    Clear PIN
                  </button>
                  <button
                    onClick={() => {
                      localStorage.removeItem('marketplace_listings');
                      toast.success('Listings cleared!');
                    }}
                    className="py-2 bg-white/5 hover:bg-white/10 rounded-lg text-white/70 text-sm"
                  >
                    Clear Listings
                  </button>
                  <button
                    onClick={() => {
                      localStorage.removeItem('gameWallet_onboarding_seen');
                      toast.success('Onboarding reset!');
                    }}
                    className="py-2 bg-white/5 hover:bg-white/10 rounded-lg text-white/70 text-sm"
                  >
                    Reset Onboarding
                  </button>
                </div>
              </div>
            )}

            {/* ECONOMY TAB */}
            {activeTab === 'economy' && (
              <div className="space-y-4">
                {/* Token Bundles */}
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                  <h3 className="text-green-400 font-bold flex items-center gap-2 mb-3">
                    <Coins className="w-4 h-4" />
                    SOL → $SLAP Bundles
                  </h3>
                  <div className="grid grid-cols-4 gap-2">
                    {ECONOMY_CONFIG.tokenBundles.map((b) => (
                      <div key={b.id} className={`bg-black/30 rounded-lg p-2 text-center ${b.label ? 'border border-yellow-500/50' : ''}`}>
                        {b.label && <p className="text-yellow-400 text-[10px] font-bold mb-1">{b.label}</p>}
                        <p className="text-yellow-400 font-bold">{b.tokens.toLocaleString()}</p>
                        <p className="text-white/40 text-xs">+{b.bonus}</p>
                        <p className="text-green-400 text-sm">{b.price} SOL</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Mystery Packs */}
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                  <h3 className="text-purple-400 font-bold flex items-center gap-2 mb-3">
                    <Store className="w-4 h-4" />
                    Mystery Pack Costs
                  </h3>
                  <div className="grid grid-cols-4 gap-2">
                    {ECONOMY_CONFIG.mysteryPacks.map((p) => (
                      <div key={p.name} className="bg-black/30 rounded-lg p-2 text-center">
                        <p className="text-white/80 text-sm">{p.name}</p>
                        <p className="text-yellow-400 font-bold">{p.cost}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Arena Tiers */}
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4">
                  <h3 className="text-cyan-400 font-bold flex items-center gap-2 mb-3">
                    <Swords className="w-4 h-4" />
                    PvP Arena Entry ($SLAP)
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(ECONOMY_CONFIG.arenaDegenCosts).map(([tier, cost]) => (
                      <div key={tier} className="bg-black/30 rounded-lg p-2 text-center">
                        <p className="text-white/80 text-sm capitalize">{tier}</p>
                        <p className="text-yellow-400 font-bold">{cost}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Fees */}
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  <h3 className="text-red-400 font-bold mb-3">Platform Fees</h3>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-black/30 rounded-lg p-2 text-center">
                      <p className="text-white/60 text-xs">Marketplace</p>
                      <p className="text-red-400 font-bold">{ECONOMY_CONFIG.fees.marketplace}</p>
                    </div>
                    <div className="bg-black/30 rounded-lg p-2 text-center">
                      <p className="text-white/60 text-xs">Swap</p>
                      <p className="text-yellow-400 font-bold">{ECONOMY_CONFIG.fees.swap}</p>
                    </div>
                    <div className="bg-black/30 rounded-lg p-2 text-center">
                      <p className="text-white/60 text-xs">Withdrawal</p>
                      <p className="text-green-400 font-bold">{ECONOMY_CONFIG.fees.withdrawal}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TOOLS TAB */}
            {activeTab === 'tools' && (
              <div className="space-y-4">
                <Button
                  onClick={handleForceWin}
                  className="w-full py-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold"
                >
                  <Trophy className="w-5 h-5 mr-2" />
                  FORCE WIN (Test Victory Animation)
                </Button>

                <Button
                  onClick={() => {
                    const testWallet = {
                      publicKey: 'TestWalletAddress1234567890ABCDEFGHIJK',
                      secretKey: 'TestSecretKey1234567890ABCDEFGHIJKLMNOPQRST',
                      shortId: 'Test...1234',
                      createdAt: Date.now()
                    };
                    localStorage.setItem('solana_wallet', JSON.stringify(testWallet));
                    localStorage.setItem('user_pin', '1234');
                    toast.success('Test wallet injected! PIN: 1234');
                  }}
                  className="w-full py-3 bg-blue-500/20 border border-blue-500/50 text-blue-400"
                >
                  Inject Test Wallet (PIN: 1234)
                </Button>

                <Button
                  onClick={() => {
                    logTransaction('TEST', { message: 'Test log entry', value: Math.random() });
                    toast.success('Test log added!');
                  }}
                  className="w-full py-3 bg-purple-500/20 border border-purple-500/50 text-purple-400"
                >
                  Add Test Log Entry
                </Button>

                <div className="bg-white/5 rounded-xl p-4 mt-4">
                  <h4 className="text-white/60 text-sm mb-2">localStorage Keys:</h4>
                  <div className="text-xs font-mono text-white/40 space-y-1 max-h-32 overflow-y-auto">
                    {Object.keys(localStorage).map((key) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-cyan-400">{key}</span>
                        <span>{(localStorage.getItem(key) || '').length} chars</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* LOGS TAB */}
            {activeTab === 'logs' && (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Button
                    onClick={handleCopyLogs}
                    disabled={logs.length === 0}
                    className="flex-1 bg-white/10 text-white"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Logs
                  </Button>
                  <Button
                    onClick={handleClearLogs}
                    disabled={logs.length === 0}
                    className="flex-1 bg-red-500/20 text-red-400"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                </div>

                {logs.length === 0 ? (
                  <div className="text-center py-8 text-white/40">
                    <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No transaction logs yet</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {logs.map((log) => (
                      <div
                        key={log.id}
                        className="bg-white/5 rounded-lg p-3 border border-white/10"
                      >
                        <div className="flex justify-between items-start">
                          <span className={`text-xs font-bold ${
                            log.type === 'ERROR' ? 'text-red-400' :
                            log.type === 'SUCCESS' ? 'text-green-400' :
                            'text-cyan-400'
                          }`}>
                            {log.type}
                          </span>
                          <span className="text-white/30 text-xs">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="text-white/60 text-xs mt-1 font-mono break-all">
                          {JSON.stringify(log.details)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-white/10 bg-black/50">
            <p className="text-white/30 text-xs text-center">
              Admin Menu v1.0 • Long press logo to access
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Long Press Hook for Logo
export const useLongPress = (callback, ms = 3000) => {
  const timeoutRef = useRef(null);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const start = () => {
    timeoutRef.current = setTimeout(() => {
      callbackRef.current();
    }, ms);
  };

  const stop = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  return {
    onMouseDown: start,
    onMouseUp: stop,
    onMouseLeave: stop,
    onTouchStart: start,
    onTouchEnd: stop,
  };
};

export default AdminMenuModal;
